import { LegacyCard, IndexTable, Text, Link, LegacyStack, Pagination, Divider } from '@shopify/polaris';
import { useEffect, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import { SET_ERROR } from '../../../context/ToastReducer';
import ToastContext from '../../../context/ToastContext';
import { getLeadsList } from '../../../services/leadsService';
import { genderLabel } from '../../../helpers/leadGender';

// Resume el interés: género + categorías (que llegan unidas por ':').
// El género se muestra SIEMPRE (null → 'Ambos') vía genderLabel.
const renderInterest = ({ interestGender, interestCategory }) => {
  const cats = interestCategory ? interestCategory.split(':').filter(Boolean).join(', ') : '';
  const parts = [];
  parts.push(genderLabel(interestGender));
  if (cats) parts.push(cats);
  return parts.length ? parts.join(' · ') : '—';
};

// Contacto preferente: email o, en su defecto, teléfono.
const renderContact = ({ email, phone }) => email || phone || '—';

const renderCenters = (deposits) => {
  if (!deposits || deposits.length === 0) return '—';
  return deposits.map((d) => (d.deposit ? d.deposit.name : '')).filter(Boolean).join(', ');
};

const renderName = ({ name, surname }) => [name, surname].filter(Boolean).join(' ');

const ListLeads = forwardRef(({ page, filters = {} }, ref) => {
  const [, dispatchToast] = useContext(ToastContext);
  const history = useHistory();

  const [response, setResponse] = useState(null);
  const [rows, setRows] = useState([]);

  useImperativeHandle(ref, () => ({
    last(e = { new: false }) {
      callDB(e.new ? Math.ceil((response.metadata.count + 1) / response.metadata.limit) : response.metadata.lastPage);
    },
  }));

  const detailUrl = (id, currentPage) => `/leads/${id}/?r=${window.btoa('/leads/page/' + currentPage)}`;

  const callDB = (p = 1, limit = 10) => {
    getLeadsList({ page: p, limit, ...filters })
      .then((res) => {
        setResponse(res.data);
        history.push(`/leads/page/${p}`);
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
        callDB();
      });
  };

  useEffect(() => {
    callDB(page);
    // eslint-disable-next-line
  }, []);

  // Recargamos al cambiar los filtros (volviendo a la primera página).
  useEffect(() => {
    callDB(1);
    // eslint-disable-next-line
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    response && setRows(response.data.map((r, k) => rowMarkup(r, k)));
    // eslint-disable-next-line
  }, [response]);

  const headingMarkup = [
    { title: 'Nombre' },
    { title: 'Contacto' },
    { title: 'Interés' },
    { title: 'Centros' },
  ];

  const rowMarkup = (lead, index) => {
    const { id } = lead;
    const currentPage = response ? response.metadata.page : page || 1;
    return (
      <IndexTable.Row id={id} key={id} position={index}>
        <IndexTable.Cell>
          <Link dataPrimaryLink url={detailUrl(id, currentPage)}></Link>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {renderName(lead)}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">{renderContact(lead)}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">{renderInterest(lead)}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">{renderCenters(lead.deposits)}</Text>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  };

  return (
    <>
      {response && (
        <>
          <IndexTable
            resourceName={{
              singular: 'Lead',
              plural: 'Leads',
            }}
            itemCount={rows.length}
            headings={headingMarkup}
            selectable={false}
          >
            {rows}
          </IndexTable>
          <Divider borderStyle="divider" />
          <LegacyCard.Section>
            <LegacyStack distribution="center">
              <Pagination
                hasPrevious={response.metadata.prev}
                onPrevious={() => callDB(response.metadata.page - 1, response.metadata.limit)}
                hasNext={response.metadata.next}
                onNext={() => callDB(response.metadata.page + 1, response.metadata.limit)}
              />
            </LegacyStack>
          </LegacyCard.Section>
        </>
      )}
    </>
  );
});

export default ListLeads;
